import { CaretLeft, CaretRight } from "@phosphor-icons/react"
import type { GridApi } from "ag-grid-community"
import React, { useCallback, useRef, useSyncExternalStore } from "react"
import { Button, IconButton } from "~/components/reusable/Button"
import Select, { type ReactSelectOption } from "~/components/reusable/Select"
import { getGridApiById, subscribeGridApiChange } from "~/hooks/useGridState"
import { trackEvent } from "~/hooks/useTrackingService"

export type PageSizeOption = "auto" | 25 | 50 | 100 | 250

function getPageSizeOptions(actualPageSize: number): ReactSelectOption<PageSizeOption>[] {
  return [
    {
      label: actualPageSize > 0 ? `Auto (${actualPageSize})` : "Auto",
      value: "auto",
    },
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
    { label: "250", value: 250 },
  ]
}

interface PaginationState {
  currentPage: number
  pageSize: number
  totalPages: number
  totalRows: number
}

const defaultPaginationState: PaginationState = {
  currentPage: 0,
  pageSize: 0,
  totalPages: 0,
  totalRows: 0,
}

// The rows at the top level, the table with everything collapsed: what's expanded under them (child
// rows, detail panels) never counts. paginationGetRowCount counts those too. Rows the table marks
// with `isFullWidthRow` aren't data (a "Create new" row), so they don't count either. The
// server-side model only knows its own count.
export function countRows(api: GridApi): number {
  const rowModelType = api.getGridOption("rowModelType") ?? "clientSide"
  if (rowModelType !== "clientSide") return api.paginationGetRowCount() ?? 0
  const isFullWidthRow = api.getGridOption("isFullWidthRow")
  const context = api.getGridOption("context")
  let count = 0
  api.forEachNodeAfterFilter((node) => {
    if (node.level !== 0 || node.footer) return
    if (isFullWidthRow?.({ rowNode: node, api, context })) return
    count += 1
  })
  return count
}

function getPaginationState(tableId: string): PaginationState {
  const api = getGridApiById(tableId)
  if (!api || api.isDestroyed?.()) return defaultPaginationState

  return {
    currentPage: api.paginationGetCurrentPage() ?? 0,
    pageSize: api.paginationGetPageSize() ?? 0,
    totalPages: api.paginationGetTotalPages() ?? 0,
    totalRows: countRows(api),
  }
}

function subscribeToGridPagination(tableId: string, callback: () => void): () => void {
  let removeApiListener: (() => void) | null = null

  const setupApiListener = () => {
    removeApiListener?.()
    removeApiListener = null
    const api = getGridApiById(tableId)
    if (api && !api.isDestroyed?.()) {
      api.addEventListener("paginationChanged", callback)
      removeApiListener = () => {
        if (!api.isDestroyed?.()) api.removeEventListener("paginationChanged", callback)
      }
    }
  }

  const unsubscribeRegistry = subscribeGridApiChange(tableId, () => {
    setupApiListener()
    callback()
  })
  setupApiListener()

  return () => {
    unsubscribeRegistry()
    removeApiListener?.()
  }
}

function useAgGridPaginationStore(tableId: string): PaginationState {
  const cachedSnapshot = useRef<PaginationState>(defaultPaginationState)
  // React reads the snapshot on every render; counting walks every row, so only a grid event
  // (which bumps this) re-reads the grid.
  const version = useRef(0)
  const readKey = useRef("")

  const subscribe = useCallback(
    (cb: () => void) =>
      subscribeToGridPagination(tableId, () => {
        version.current += 1
        cb()
      }),
    [tableId],
  )

  const getSnapshot = useCallback(() => {
    const key = `${tableId}:${version.current}`
    if (readKey.current === key) return cachedSnapshot.current
    readKey.current = key
    const next = getPaginationState(tableId)
    const prev = cachedSnapshot.current
    if (
      next.currentPage === prev.currentPage &&
      next.pageSize === prev.pageSize &&
      next.totalPages === prev.totalPages &&
      next.totalRows === prev.totalRows
    )
      return prev
    cachedSnapshot.current = next
    return next
  }, [tableId])

  return useSyncExternalStore(subscribe, getSnapshot)
}

interface CustomPaginationProps {
  tableId: string
  selectedPageSize: PageSizeOption
  onPageSizeChange: (pageSize: PageSizeOption) => void
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  tableId,
  selectedPageSize,
  onPageSizeChange,
}) => {
  const { currentPage, pageSize, totalPages, totalRows } = useAgGridPaginationStore(tableId)

  const getApi = useCallback(() => getGridApiById(tableId), [tableId])

  const handleFirstPage = useCallback(() => {
    const api = getApi()
    if (!api || api.isDestroyed?.()) return
    trackEvent("Pagination First Page Clicked", {
      "Current Page": currentPage + 1,
      "Total Pages": totalPages,
    })
    api.paginationGoToFirstPage()
  }, [getApi, currentPage, totalPages])

  const handlePreviousPage = useCallback(() => {
    const api = getApi()
    if (!api || api.isDestroyed?.()) return
    trackEvent("Pagination Previous Page Clicked", {
      "Current Page": currentPage + 1,
      "Total Pages": totalPages,
    })
    api.paginationGoToPreviousPage()
  }, [getApi, currentPage, totalPages])

  const handleNextPage = useCallback(() => {
    const api = getApi()
    if (!api || api.isDestroyed?.()) return
    trackEvent("Pagination Next Page Clicked", {
      "Current Page": currentPage + 1,
      "Total Pages": totalPages,
    })
    api.paginationGoToNextPage()
  }, [getApi, currentPage, totalPages])

  const handleLastPage = useCallback(() => {
    const api = getApi()
    if (!api || api.isDestroyed?.()) return
    trackEvent("Pagination Last Page Clicked", {
      "Current Page": currentPage + 1,
      "Total Pages": totalPages,
    })
    api.paginationGoToLastPage()
  }, [getApi, currentPage, totalPages])

  return (
    // @container: respond to the bar's own width, not the viewport. Below @2xl the two
    // groups stack (narrow drawers); @2xl+ is the original single row.
    <div className="@container w-full">
      <div className="flex flex-col gap-2 @2xl:h-[28px] @2xl:flex-row @2xl:items-center @2xl:justify-between">
        {/* Left side: Total rows display */}
        <div className="flex items-center gap-2">
          <span className="paragraph-base text-fg-base whitespace-nowrap">
            Total rows: <span className="font-medium">{totalRows.toLocaleString()}</span>
          </span>
          <PageSizeSelect
            selectedPageSize={selectedPageSize}
            actualPageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </div>

        {/* Right side: Page navigation */}
        <div className="flex items-center justify-between gap-3 @2xl:justify-end">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onFirst={handleFirstPage}
            onPrevious={handlePreviousPage}
            onNext={handleNextPage}
            onLast={handleLastPage}
          />
        </div>
      </div>
    </div>
  )
}

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  onFirst: () => void
  onPrevious: () => void
  onNext: () => void
  onLast: () => void
}

export function PaginationControls({
  currentPage,
  totalPages,
  onFirst,
  onPrevious,
  onNext,
  onLast,
}: PaginationControlsProps) {
  const isFirstPage = currentPage === 0
  const isLastPage = currentPage >= totalPages - 1 || totalPages === 0
  return (
    <>
      <span className="paragraph-base text-fg-base whitespace-nowrap">
        Page {totalPages > 0 ? currentPage + 1 : 0} of {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral"
          size="sm"
          type="button"
          onClick={onFirst}
          disabled={isFirstPage}
          aria-label="First page"
        >
          First
        </Button>
        <IconButton
          variant="neutral"
          size="sm"
          type="button"
          onClick={onPrevious}
          disabled={isFirstPage}
          aria-label="Previous page"
        >
          <CaretLeft />
        </IconButton>
        <IconButton
          variant="neutral"
          size="sm"
          type="button"
          onClick={onNext}
          disabled={isLastPage}
          aria-label="Next page"
        >
          <CaretRight />
        </IconButton>
        <Button
          variant="neutral"
          size="sm"
          type="button"
          onClick={onLast}
          disabled={isLastPage}
          aria-label="Last page"
        >
          Last
        </Button>
      </div>
    </>
  )
}

interface PageSizeSelectProps {
  selectedPageSize: PageSizeOption
  actualPageSize: number
  onPageSizeChange: (pageSize: PageSizeOption) => void
}

export function PageSizeSelect({
  selectedPageSize,
  actualPageSize,
  onPageSizeChange,
}: PageSizeSelectProps) {
  const pageSizeOptions = getPageSizeOptions(selectedPageSize === "auto" ? actualPageSize : 0)
  const selectedOption =
    pageSizeOptions.find((opt) => opt.value === selectedPageSize) ?? pageSizeOptions[0]
  const handlePageSizeChange = (option: ReactSelectOption<PageSizeOption> | null) => {
    if (!option) return
    trackEvent("Pagination Page Size Changed", {
      "New Page Size": option.value,
      "Previous Page Size": selectedPageSize,
    })
    onPageSizeChange(option.value)
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="paragraph-base text-fg-base whitespace-nowrap">Rows per page:</span>
      {/* Fixed width so the label ("Auto (250)") never collapses to "A..." when tight. */}
      <div className="w-28 shrink-0">
        <Select<ReactSelectOption<PageSizeOption>>
          options={pageSizeOptions}
          value={selectedOption}
          onChange={handlePageSizeChange}
          isSearchable={false}
          size="sm"
        />
      </div>
    </div>
  )
}
